import { HttpErrorResponse } from '@angular/common/http';
import { Component, ViewEncapsulation, OnDestroy, HostBinding } from '@angular/core';
import { FormControl, Validators, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';

import { UserService } from '../../services/user.service';
import { simpleTransitions } from '../../shared/animations/simple-transitions';
import { decode } from '../../shared/utils/decode';
import { LoginService } from '../../services/login.service';
import { SessionContext } from '../../models/session-context';
import { BUILD_NUMBER } from '../../config';
import { LoadingSpinnerComponent } from '../utils/loading-spinner.component';

import { PrivacySettingComponent } from './privacy-setting.component';

@Component({
	selector: 'copo-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.css'],
	animations: simpleTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	standalone: true,
	imports: [
		NgIf,
		ReactiveFormsModule,
		MatIconModule,
		NgSwitch,
		TranslateModule,
		NgSwitchCase,
		NgSwitchDefault,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyButtonModule,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyFormFieldModule,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyInputModule,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyCheckboxModule,
		LoadingSpinnerComponent,
		RouterLink
	]
})
export class LoginComponent implements OnDestroy {
	@HostBinding('class.mat-typography') matTypography = true;
	@HostBinding('class.full-height') fullHeight = true;
	readonly buildNumber = BUILD_NUMBER;

	submitting = false;

	loginFormGroup = new FormGroup({
		username: new FormControl('', { validators: Validators.required }),
		password: new FormControl('', { validators: Validators.required }),
		rememberMe: new FormControl(false)
	});

	linkingFormGroup = new FormGroup({
		username: new FormControl('', { validators: Validators.required }),
		password: new FormControl('', { validators: Validators.required })
	});

	errorStatus: string | undefined;
	state: string | undefined;
	responseType: string | undefined;
	redirectUri: string | undefined;
	clientId: string | undefined;
	codeChallenge: string | undefined;
	codeChallengeMethod: string | undefined;
	submittingTimeout: number | undefined;
	closeModal = false;
	isPortal = false;
	sessionContext: SessionContext | undefined;
	sessionSub: Subscription;
	sessionLoaded = false;
	displayLinkAccount = false;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly loginService: LoginService,
		// eslint-disable-next-line deprecation/deprecation
		public dialog: MatDialog,
		private readonly userService: UserService
	) {
		const params = <Record<string, string | undefined>> this.route.snapshot.queryParams;
		this.errorStatus = decode(params['error_status']);
		this.state = params['state'];
		this.responseType = params['response_type'];
		this.redirectUri = params['redirect_uri'];
		this.clientId = params['client_id'];
		this.codeChallenge = params['code_challenge'];
		this.codeChallengeMethod = params['code_challenge_method'];
		if (!this.clientId) {
			this.clientId = '7U2EyJ8Xn5UvRPWA';
			this.responseType = 'code';
		}
		if (this.clientId === '7U2EyJ8Xn5UvRPWA') {
			this.isPortal = true;
			if (this.userService.readCookie('cookieInfoRead').length === 0) {
				this.openPrivacyPolicyDialog();
			}
		}

		this.sessionSub = this.loginService.getSessionContext(this.clientId, this.redirectUri).subscribe({
			next: context => {
				this.sessionLoaded = true;
				this.sessionContext = context;
				this.errorStatus = context.errorStatus;
			},
			error: () => {
				this.sessionLoaded = true;
			}
		});
	}

	get year() {
		return new Date().getFullYear();
	}

	async onSubmit() {
		if (!this.loginFormGroup.valid) {
			return;
		}
		this.errorStatus = undefined;
		try {
			const val = <Record<string, string>> this.loginFormGroup.value;

			this.submitting = true;
			this.loginFormGroup.disable();

			await this.loginService.challengeUser(
				val['username'],
				val['password'],
				val['rememberMe'],
				this.state,
				this.responseType,
				this.redirectUri,
				this.clientId
			);

			location.replace(this.getAuthRequestURL().toString());
			// reenable after 30 seconds (an error probably happened on load)
			this.reEnableAfter(30_000);
		} catch (err: unknown) {
			// reenable after 3 seconds to prevent spamming
			this.reEnableAfter(3000);
			if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
				console.error(err);
				return;
			}
			if (typeof err.error === 'object') {
				this.errorStatus = (<{ status: string }>err.error).status;
				this.errorStatus ||= 'unknown';

				if (this.errorStatus === 'expired_password') {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
					const sessionToken = (<{ id: string }>err.error.token).id;
					await this.router.navigate(
						['expired-password'],
						{ queryParams: { session_token: sessionToken }, replaceUrl: true }
					);
				}
			} else {
				console.error(err);
			}
		}
	}

	goToSSO() {
		this.submitting = true;
		this.loginFormGroup.disable();
		const ssoUrl = new URL('/rest/v1/azure/login', location.origin);
		const params = ssoUrl.searchParams;
		params.set('state', this.getAuthRequestURL().toString());
		location.replace(ssoUrl.toString());
		// reenable after 30 seconds (an error probably happened on load)
		this.reEnableAfter(30_000);
	}

	async onLinkingSubmit() {
		if (!this.linkingFormGroup.valid) {
			return;
		}
		this.errorStatus = undefined;
		try {
			const val = <Record<string, string>> this.linkingFormGroup.value;

			this.submitting = true;
			this.linkingFormGroup.disable();

			await this.loginService.linkUser(
				val['username'],
				val['password'],
				this.state,
				this.responseType,
				this.redirectUri,
				this.clientId
			);

			location.replace(this.getAuthRequestURL().toString());
			// reenable after 30 seconds (an error probably happened on load)
			this.reEnableAfter(30_000);
		} catch (err) {
			// reenable after 3 seconds to prevent spamming
			this.reEnableAfter(3000);
			if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
				console.error(err);
				return;
			}
			if (typeof err.error === 'object') {
				this.errorStatus = (<{ status: string }> err.error).status;
				this.errorStatus ||= 'unknown';
			} else {
				console.error(err);
			}
		}
	}

	ngOnDestroy() {
		this.sessionSub.unsubscribe();
		if (this.submittingTimeout !== undefined) {
			clearTimeout(this.submittingTimeout);
			this.submittingTimeout = undefined;
		}
	}

	logout() {
		const logoutUrl = new URL('/rest/auth/logout', location.origin);
		const logoutParams = logoutUrl.searchParams;
		const callbackUrl = this.getAuthRequestURL();
		logoutParams.set('callback', String(callbackUrl));

		this.submitting = true;
		this.loginFormGroup.disable();
		location.replace(logoutUrl.toString());
		this.reEnableAfter(30_000);
	}

	continue() {
		const authUrl = this.getAuthRequestURL();

		this.submitting = true;
		this.loginFormGroup.disable();
		location.replace(authUrl.toString());
		this.reEnableAfter(30_000);
	}

	getAuthRequestURL() {
		return this.loginService
			.createUrl(
				'/rest/auth/oauth2',
				this.state,
				this.responseType,
				this.redirectUri,
				this.clientId,
				this.codeChallenge,
				this.codeChallengeMethod
			);
	}

	linkYourAccount() {
		this.errorStatus = undefined;
		this.displayLinkAccount = true;
	}

	backFromLinkYourAccount() {
		this.errorStatus = undefined;
		this.displayLinkAccount = false;
	}

	openPrivacyPolicyDialog() {
		this.dialog.open(
			PrivacySettingComponent,
			{
				panelClass: 'privacy-modal',
				width: '1000px',
				maxHeight: '90vh',
				disableClose: true,
				autoFocus: false
			}
		);
	}

	private reEnableAfter(time: number) {
		this.submittingTimeout = setTimeout(
			() => {
				this.submittingTimeout = undefined;
				this.submitting = false;
				this.loginFormGroup.enable();
				this.linkingFormGroup.enable();
			},
			time
		);
	}
}
